import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { toast } from 'sonner';

import { useSubmitArtifactAuditReviewMutation } from '@/services/api/artifactAuditService';
import { ArtifactAudit } from '@/services/types/blob-types';

interface AuditWarningBannerProps {
  audit: ArtifactAudit;
  artifactId: string;
  matterId: string;
}

export const AuditWarningBanner: React.FC<AuditWarningBannerProps> = ({
  audit,
  artifactId,
  matterId,
}) => {
  const [submitReview, { isLoading }] = useSubmitArtifactAuditReviewMutation();

  const confidencePct = audit.confidence != null ? Math.round(audit.confidence * 100) : null;
  const matchText = confidencePct != null ? ` (${confidencePct}% match)` : '';

  const handleKeep = async () => {
    try {
      await submitReview({
        artifactId,
        matterId,
        blobId: audit.blob_id,
        body: { decision: 'override_safe', notes: 'Marked safe from document preview' },
      }).unwrap();
      toast.success('Document marked as safe');
    } catch {
      toast.error('Failed to submit review');
    }
  };

  const handleDelete = async () => {
    try {
      await submitReview({
        artifactId,
        matterId,
        blobId: audit.blob_id,
        body: { decision: 'confirm_mismatch', notes: 'Confirmed mismatch from document preview' },
      }).unwrap();
      toast.success('Document confirmed as mismatch');
    } catch {
      toast.error('Failed to submit review');
    }
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-[#FFFBEB] border-b border-[#FDE68A]">
      <ExclamationTriangleIcon className="w-4 h-4 text-[#92400E] flex-shrink-0" />
      <span className="flex-1 text-[11px] font-medium text-[#92400E] leading-[14px] truncate">
        Flagged — may not be related to this matter{matchText}
      </span>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          type="button"
          className="px-2.5 py-[3px] rounded text-[11px] font-medium bg-gray-800 text-white hover:bg-gray-900 transition-colors disabled:opacity-50"
          onClick={handleKeep}
          disabled={isLoading}
        >
          Keep
        </button>
        <button
          type="button"
          className="px-2.5 py-[3px] rounded text-[11px] font-medium bg-red-50 border border-red-300 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
          onClick={handleDelete}
          disabled={isLoading}
        >
          Delete
        </button>
      </div>
    </div>
  );
};
